import PropTypes from "prop-types";
import { useTheme, css } from "@emotion/react";
import { createRef, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { em, emu } from "@util/convertUnits";
import useBreakpoints from "@util/breakpoints";
import { useHistory } from "react-router-dom";
import NavBarMobileAccordian from "./NavBarMobileAccordian";
import useOutsideClick from "../../helpers/useOutsideClick";
import { ButtonPill } from "@/base";
import NavImageTile from "@/core/navigation/NavImageTile";
import { firstTile } from "@/core/navigation/navigation.data";

const NavBarMobile = ({ navData }) => {
  const theme = useTheme();

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const menuRef = createRef();
  const history = useHistory();
  let menuClass = "is-" + isOpen;
  const mobileNav = useRef();

  useEffect(() => {
    let mainNav = document.getElementById("StickyMainNav");

    if (mobileNav.current && mainNav) {
      mobileNav.current.style.height = `calc(100vh - ${
        mainNav.offsetHeight - 10
      }px)`;
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
  }, [isOpen]);

  const menuBtn = useBreakpoints([270])({
    width: "4rem",
    height: "4rem",
    padding: "0.8rem",
    marginLeft: "auto",
    marginRight: ["auto", "0"],
  });

  const menuCSS = useBreakpoints([270])({
    display: "block",
    width: "4rem",
    height: "4rem",
    padding: "0.8rem",
    marginLeft: "auto",
    marginRight: ["auto", "0"],
    textIndent: "-10rem",
    backgroundColor: "transparent",
    backgroundImage: "url(/images/nav-icons/icon-menu.svg)",
    backgroundPosition: "50% 50%",
    backgroundRepeat: "no-repeat",
    border: "none",
    overflow: "hidden",

    "&.is-true": {
      backgroundImage: "url(/images/nav-icons/icon-close.svg)",
    },
  });

  const utilCtaCssMobile = css(`
    display: flex;
    flex-direction: column;
    align-items: center;
  `);

  const callUs = css(`
    padding-top: 6px;
  `);

  const callUsNumber = css(`
    font-weight: bold;
  `);

  const ddStyle = useBreakpoints([theme.breakpoint.ns])({
    position: "absolute",
    left: ["0", "-1.6rem"],
    width: ["100%", "calc(100% + 3.2rem)"],
    marginTop: "2.4rem",
    fontFamily: theme.typography.button.fontFamily,
    backgroundColor: "#ffffff",
    borderTop: "1px solid #e1e1e1",
    boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.05)",
    overflowY: "scroll",
    overflowX: "hidden",
  });

  const searchCss = css(`
		position: relative;
		display: block;
		padding-top: 2.9rem;
		padding-bottom: 1rem;
		border-bottom: 2px solid #979797;
	`);

  const searchInput = css(`
    width: 100%;
    height: 4rem;
    padding-left: 0.8rem;
    padding-right: 4rem;
    font-size: ${em(20)};
		line-height: ${emu(26, 20)};
    background-color: transparent;
    border: none;
  `);

  const searchIconCSS = css(`
    position: absolute;
		bottom: 1rem;
		right: 0;
		z-index: 1;
    display: block;
    width: 4rem;
    height: 4rem;
    padding: 0.8rem;
    background-image: url(/images/common-icons/icon-search-black.svg);
    background-repeat: no-repeat;
    background-position: center center;
  `);

  const styleMenu = css(`
		padding-top: 2.4rem;
		padding-bottom: 3.2rem;
		// overflow-y: scroll;
		// height: 100vh;
		
		> ul {
			padding: 0;
			margin: 0;
			list-style: none;

			> li {
				margin-bottom: 0;

				a {
					text-decoration: none;
				}
			}
		}
  `);

  const ddBtn = css(`
		padding-top: 2.4rem;
		padding-bottom: 2.4rem;
		font-weight: 300;
	`);

  useOutsideClick(menuRef, () => {
    if (isOpen) setIsOpen(false);
  });

  const handleToggleMenu = (ev) => {
    if (ev) ev.preventDefault();
    setIsOpen(!isOpen);
  };

  const handleMenuItemClick = () => {
    handleToggleMenu();
    window.scrollTo(0, 0);
  };

  const handleSearch = () => {
    const url = `/search/${searchValue}`;
    setSearchValue("");
    setIsOpen(false);
    history.push(url);
  };

  const handleOnEnter = (e) => {
    if (e.keyCode === 13) {
      handleSearch();
    }
  };

  const renderMenu = () => (
    <div css={ddStyle} ref={mobileNav}>
      <div className="container">
        <div css={searchCss}>
          <input
            type="text"
            placeholder="Search"
            value={searchValue}
            className="search_input"
            onKeyDown={handleOnEnter}
            css={searchInput}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <span
            className="search_icon"
            role="button"
            tabIndex={0}
            css={searchIconCSS}
            onClick={handleSearch}
            onKeyDown={() => {}}
          ></span>
        </div>
      </div>
      <div css={styleMenu}>
        <ul>
          {navData.map((menuItem) => {
            return menuItem.children ? (
              // Menu items that expand/collapse
              <NavBarMobileAccordian
                key={`${menuItem.name}`}
                menuItem={menuItem}
                handleMenuItemClick={handleMenuItemClick}
              />
            ) : (
              // Menu items that do not expand/collapse
              <li key={`${menuItem.name}${menuItem.url}`}>
                <div className="px-3 px-ns-4" css={ddBtn}>
                  <Link to={menuItem.url} onClick={handleMenuItemClick}>
                    {menuItem.name}
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <NavImageTile slug={firstTile} size={1} onClick={handleMenuItemClick} />

      <div
        className={
          "d-flex flex-row pt-3 pb-8 mx-2 mx-xs-4 mt-2 justify-content-end"
        }
      >
        <div className="utilCtaCssMobile" css={utilCtaCssMobile}>
            <ButtonPill to={"/request-quote"} buttonTheme="dark" onClick={handleMenuItemClick}>
                Request Quote
            </ButtonPill>
            <div className="callUs" css={callUs}>Call us <a href="tel:1-888-221-0141" className="callUsNumber" css={callUsNumber}>888.221.0141</a></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={menuBtn} ref={menuRef}>
      <button className={menuClass} css={menuCSS} onClick={handleToggleMenu}>
        Menu
      </button>
      {isOpen && renderMenu()}
    </div>
  );
};

NavBarMobile.propTypes = {
  navData: PropTypes.array,
};

export default NavBarMobile;
